import React, { useCallback, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, Plus, Printer, ScanLine, Wifi } from 'lucide-react';
import { forgetLocationWifi, loadDevices, printTestLabel, removeDevice, setPrinterLabelSize } from './api';
import { DeviceDrawing } from './DeviceDrawings';
import DeviceWizard from './DeviceWizard';
import { LabFacts, LabResource, labWhen } from './LaboratoryParts';
import { LabDrawer } from './LaboratoryVisuals';
import { Empty, Head, Pill, SummaryBar, SummaryItem } from './parts';
import { useLiveResource } from './useLive';
import { hasTx, tx } from './i18n';
import { errorMessage } from './ui';
import './devices.css';

const text = (key, values) => tx(`ui.devices.${key}`, values);
// A printer's line is checked often enough that switching one on shows here
// without a reload.
const REFRESH_MS = 10000;

// What a device is doing right now, as one word and a colour.
export function deviceStatus(device) {
  if (device.type.category !== 'printer') return { key: 'scanner', tone: 'flat' };
  if (!device.paired) return { key: 'unpaired', tone: 'flat' };
  if (!device.online) return { key: 'offline', tone: 'bad' };
  return device.ready ? { key: 'online', tone: 'ok' } : { key: 'connecting', tone: 'warn' };
}

// The printer names its own conditions in English capitals; the ones a person
// can do something about have their own words.
export function conditionText(condition) {
  const key = `ui.devices.condition.${String(condition).trim().toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
  return hasTx(key) ? tx(key) : text('condition.other');
}

const typeNote = type => (hasTx(`ui.devices.type.${type.code}`) ? tx(`ui.devices.type.${type.code}`) : text(`purpose.${type.purpose}`));

function LabelSize({ device, onSaved }) {
  const [width, setWidth] = useState(device.label ? String(device.label.widthMm) : '');
  const [height, setHeight] = useState(device.label ? String(device.label.heightMm) : '');
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const valid = value => /^[0-9]{2,3}(\.[0-9])?$/.test(value) && Number(value) >= 10 && Number(value) <= 250;
  const changed = !device.label || Number(width) !== device.label.widthMm || Number(height) !== device.label.heightMm;
  async function save(event) {
    event.preventDefault();
    if (busy || !valid(width) || !valid(height)) return;
    setBusy(true); setError('');
    try { await setPrinterLabelSize(device.deviceId, Number(width), Number(height)); await onSaved(); }
    catch (caught) { setError(errorMessage(caught)); }
    finally { setBusy(false); }
  }
  return <form className="xo-dev-label-size" onSubmit={save}>
    <h3>{text('label_size')}</h3>
    <div>
      <label><span>{text('width_mm')}</span><input inputMode="decimal" value={width} onChange={event => setWidth(event.target.value)} disabled={busy} /></label>
      <label><span>{text('height_mm')}</span><input inputMode="decimal" value={height} onChange={event => setHeight(event.target.value)} disabled={busy} /></label>
      <button type="submit" className="xo-btn-2" disabled={busy || !changed || !valid(width) || !valid(height)}>{text(busy ? 'saving' : 'save')}</button>
    </div>
    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
  </form>;
}

function DeviceDetails({ device, canManage, onChanged, onPair, onTestScanner, onClose }) {
  const [busy, setBusy] = useState(''), [error, setError] = useState(''), [notice, setNotice] = useState('');
  const [confirming, setConfirming] = useState(false);
  const inFlight = useRef(false);
  const status = deviceStatus(device);
  const printer = device.type.category === 'printer';
  async function act(name, work, done) {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(name); setError(''); setNotice('');
    try { await work(); if (done) setNotice(done); }
    catch (caught) { setError(errorMessage(caught)); }
    finally { inFlight.current = false; setBusy(''); }
  }
  return <LabDrawer title={device.type.model} eyebrow={`${device.type.manufacturer} · ${typeNote(device.type)}`} onClose={onClose}>
    <div className={`xo-dev-drawer-art is-${status.key}`}><DeviceDrawing category={device.type.category} purpose={device.type.purpose} /></div>
    <div className="xo-lab-detail-tags xo-dev-tags">
      <Pill tone={status.tone}>{text(`status.${status.key}`)}</Pill>
      {device.conditions.map(condition => <Pill key={condition} tone="warn">{conditionText(condition)}</Pill>)}
    </div>
    <LabFacts entries={[
      [text('code'), <code>{device.deviceId}</code>],
      [text('used_for'), text(`purpose.${device.type.purpose}`)],
      ...(device.serialNumber ? [[text('serial'), <code>{device.serialNumber}</code>]] : []),
      ...(device.firmware ? [[text('firmware'), device.firmware]] : []),
      ...(printer && device.label ? [[text('label_size'), text('label_size_value', { width: device.label.widthMm, height: device.label.heightMm })]] : []),
      ...(printer && device.pairedAt !== null ? [[text('paired_at'), labWhen(device.pairedAt)]] : []),
      ...(printer ? [[text('last_seen_label'), device.lastSeenAt === null ? text('never_seen') : labWhen(device.lastSeenAt)]] : []),
    ]} />
    {!printer && <section className="xo-dev-how">
      <h3>{text('scanner_how_title')}</h3>
      <ol><li>{text('scanner_how_1')}</li><li>{text('scanner_how_2')}</li><li>{text('scanner_how_3')}</li></ol>
    </section>}
    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
    {notice !== '' && <p className="xo-alert t-ok" role="status">{notice}</p>}
    {canManage && <section className="xo-dev-actions">
      {printer && <button type="button" className="xo-btn" onClick={() => onPair(device)} disabled={busy !== ''}>
        <Wifi aria-hidden="true" />{text(device.paired ? 'connect_again' : 'connect')}</button>}
      {printer && device.paired && <button type="button" className="xo-btn-2" disabled={busy !== '' || !device.ready}
        onClick={() => act('test', () => printTestLabel(device.deviceId), text('test_label_done'))}>
        <Printer aria-hidden="true" />{text(busy === 'test' ? 'printing' : 'test_label')}</button>}
      {!printer && <button type="button" className="xo-btn" onClick={() => onTestScanner(device)} disabled={busy !== ''}>
        <ScanLine aria-hidden="true" />{text('test_scanner')}</button>}
    </section>}
    {canManage && printer && <LabelSize key={`${device.deviceId}-${device.label ? device.label.widthMm : ''}`} device={device} onSaved={onChanged} />}
    {canManage && <section className="xo-dev-remove">
      {confirming ? <>
        <p>{text('remove_confirm')}</p>
        <div><button type="button" className="xo-btn-2" onClick={() => setConfirming(false)} disabled={busy !== ''}>{text('cancel')}</button>
          <button type="button" className="xo-btn xo-dev-danger" disabled={busy !== ''}
            onClick={() => act('remove', async () => { await removeDevice(device.deviceId); await onChanged(); onClose(); })}>{text(busy === 'remove' ? 'removing' : 'remove_yes')}</button></div>
      </> : <button type="button" className="xo-link t-bad" onClick={() => setConfirming(true)}>{text('remove')}</button>}
    </section>}
  </LabDrawer>;
}

function DeviceCard({ device, onOpen }) {
  const status = deviceStatus(device);
  const printer = device.type.category === 'printer';
  return <li>
    <button type="button" className={`xo-dev-card is-${status.key}`} onClick={onOpen} aria-label={`${device.type.model} · ${device.deviceId}`}>
      <span className="xo-dev-card-art"><DeviceDrawing category={device.type.category} purpose={device.type.purpose} /></span>
      <span className="xo-dev-card-name"><strong>{device.type.model}</strong><small>{typeNote(device.type)}</small></span>
      <span className="xo-dev-card-state">
        <Pill tone={status.tone}>{text(`status.${status.key}`)}</Pill>
        {device.conditions.map(condition => <Pill key={condition} tone="warn">{conditionText(condition)}</Pill>)}
      </span>
      <span className="xo-dev-card-foot">
        <code>{device.deviceId}</code>
        <small>{printer ? (device.paired && !device.online && device.lastSeenAt !== null ? text('last_seen', { when: labWhen(device.lastSeenAt) })
          : device.serialNumber || '') : ''}</small>
        <ChevronRight aria-hidden="true" />
      </span>
    </button>
  </li>;
}

export default function XomicsDevices() {
  const [params, setParams] = useSearchParams();
  const [wizard, setWizard] = useState(null);
  const [wifiBusy, setWifiBusy] = useState(false), [wifiError, setWifiError] = useState('');
  const load = useCallback(() => loadDevices(), []);
  const resource = useLiveResource([], load, { reconcileMs: REFRESH_MS });
  const close = () => { const next = new URLSearchParams(params); next.delete('device'); setParams(next, { replace: true }); };
  async function forgetWifi() {
    if (wifiBusy) return;
    setWifiBusy(true); setWifiError('');
    try { await forgetLocationWifi(); await resource.refresh(); } catch (caught) { setWifiError(errorMessage(caught)); } finally { setWifiBusy(false); }
  }
  return <div className="xo-platform-page xo-dev-page">
    <Head title={text('title')} subtitle={text('subtitle')}>
      {resource.data && resource.data.canManage && <button type="button" className="xo-btn" onClick={() => setWizard({ mode: 'add' })}>
        <Plus aria-hidden="true" />{text('add')}</button>}
    </Head>
    <LabResource resource={resource}>{(data) => {
      const printers = data.devices.filter(device => device.type.category === 'printer');
      const scanners = data.devices.filter(device => device.type.category === 'scanner');
      const selected = data.devices.find(device => device.deviceId === params.get('device'));
      return <>
        {data.devices.length > 0 && <SummaryBar>
          <SummaryItem value={printers.length} label={text('printers')} />
          <SummaryItem value={printers.filter(device => device.online).length} label={text('online')} tone="ok" />
          <SummaryItem value={printers.filter(device => device.paired && !device.online).length} label={text('offline')}
            tone={printers.some(device => device.paired && !device.online) ? 'bad' : 'flat'} />
          <SummaryItem value={scanners.length} label={text('scanners')} />
        </SummaryBar>}
        {data.devices.length === 0 && <Empty title={text('empty_title')} detail={text(data.canManage ? 'empty_detail' : 'empty_detail_reader')} />}
        {[['printers', printers], ['scanners', scanners]].map(([group, devices]) => devices.length > 0 && <section key={group} className="xo-dev-group">
          <h2>{text(group)}</h2>
          <ul className="xo-dev-grid" aria-label={text(group)}>{devices.map(device => <DeviceCard key={device.deviceId} device={device}
            onOpen={() => setParams({ device: device.deviceId })} />)}</ul>
        </section>)}
        {data.canManage && data.wifiNetwork !== null && <section className="xo-dev-wifi">
          <Wifi aria-hidden="true" />
          <div><strong>{text('wifi_saved')}</strong><span>{data.wifiNetwork}</span></div>
          <button type="button" className="xo-link" onClick={forgetWifi} disabled={wifiBusy}>{text('wifi_forget')}</button>
          {wifiError !== '' && <p className="xo-alert t-bad" role="alert">{wifiError}</p>}
        </section>}
        {selected && wizard === null && <DeviceDetails key={selected.deviceId} device={selected} canManage={data.canManage} onChanged={resource.refresh}
          onPair={device => setWizard({ mode: 'printer', device })} onTestScanner={device => setWizard({ mode: 'scanner', device })} onClose={close} />}
        {wizard !== null && <DeviceWizard start={wizard} onChanged={resource.refresh}
          onClose={() => { setWizard(null); resource.refresh(); }} />}
      </>;
    }}</LabResource>
  </div>;
}
